import React, { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { NEET_PHYSICS_CH22_QUESTIONS } from "../data/neetPhysicsCh22Questions";

// ─────────────────────────────────────────────────────────────────────────────
// NEET Physics — Chapter 22: Alternating Current (NCERT Class 12 Physics).
// Content is drafted from the NCERT syllabus topic list; each concept has an
// explanation plus a worked example. NOT YET human-reviewed by a subject-matter
// expert — treat as a first draft and verify against NCERT before exam use.
// ─────────────────────────────────────────────────────────────────────────────

const SECTIONS = ["AC Basics & RMS Values", "AC Through R, L, C", "LCR Series Circuit & Impedance", "Resonance & Power", "Transformer"];

const SECTION_COLORS = {
  "AC Basics & RMS Values":          "#2563eb",
  "AC Through R, L, C":              "#7c3aed",
  "LCR Series Circuit & Impedance":  "#dc2626",
  "Resonance & Power":               "#d97706",
  "Transformer":                     "#0d9488",
};

const CONCEPTS = [
  // ── AC Basics & RMS Values ──────────────────────────────────────────────────
  { section: "AC Basics & RMS Values", title: "AC voltage and current — instantaneous, peak and phase",
    explanation: "An alternating current/voltage varies sinusoidally with time: i = I0 sin(wt + phi), v = V0 sin(wt), where I0/V0 is the peak (amplitude) value, w = 2*pi*f is the angular frequency, and phi is the phase. The period T = 2*pi/w = 1/f is the time for one complete cycle.",
    example: `Indian AC mains: f = 50 Hz
w = 2*pi*f = 2*pi(50) = 314 rad/s
T = 1/f = 1/50 = 0.02 s = 20 ms

If peak voltage V0 = 311 V:  v(t) = 311 sin(314 t)  volts` },
  { section: "AC Basics & RMS Values", title: "Average value of AC over a full cycle and half cycle",
    explanation: "Over one COMPLETE cycle, the average value of a pure sinusoid is exactly ZERO, since the positive and negative half-cycles are mirror images and cancel. Over just HALF a cycle, the average value is nonzero: Iavg = 2*I0/pi (about 0.637*I0) — this is the value relevant to devices like rectifier-based DC meters.",
    example: `Full cycle average = 0  (always, for a pure sine wave)

Half cycle average:
Iavg = (2/pi) * I0
If I0 = 10 A:  Iavg = 2(10)/pi = 6.37 A` },
  { section: "AC Basics & RMS Values", title: "RMS (root mean square) value — Irms = I0/√2",
    explanation: "The RMS (root-mean-square) value is found by squaring i(t), averaging the square over one cycle, then taking the square root of that average. Since the mean of sin^2(wt) over a cycle is 1/2, this gives Irms = I0/sqrt(2) ~ 0.707*I0, and likewise Vrms = V0/sqrt(2).",
    example: `mean of sin^2(wt) over a cycle = 1/2

Irms = sqrt( mean of i^2 ) = I0 / sqrt(2) ~ 0.707 I0
Vrms = V0 / sqrt(2)

If I0 = 10 A:  Irms = 10/1.414 = 7.07 A` },
  { section: "AC Basics & RMS Values", title: "Why RMS value is used — equivalence to DC heating effect",
    explanation: "RMS current is defined so that it dissipates the SAME average power in a resistor as an equal-valued DC current would — hence it's also called the 'virtual' or 'effective' value. AC ammeters/voltmeters (hot-wire type) sense heating (i^2*R) and so are calibrated to display RMS values directly, which is why household supply is rated '220 V AC' (the rms value), not its ~311 V peak.",
    example: `DC of value Idc dissipates: P = Idc^2 * R
AC average power: Pavg = (1/2)*I0^2*R = (I0/sqrt(2))^2 * R = Irms^2 * R

Setting Idc = Irms gives IDENTICAL heating
-> "220 V AC mains" means Vrms = 220 V (peak is ~311 V)` },

  // ── AC Through R, L, C ───────────────────────────────────────────────────────
  { section: "AC Through R, L, C", title: "AC through a pure resistor — current and voltage in phase",
    explanation: "For v = V0 sin(wt) applied across a pure resistor R, Ohm's law holds at every instant: i = v/R = (V0/R) sin(wt) = I0 sin(wt). Current and voltage rise, peak, and fall together — zero phase difference. Instantaneous power p = vi = V0*I0*sin^2(wt) is never negative, and the average power is Vrms*Irms.",
    example: `V0 = 200 V, R = 50 ohm
I0 = V0/R = 200/50 = 4 A   (in phase with V)

Average power = Vrms * Irms = (200/1.414)(4/1.414) = 141.4 * 2.83 = 400 W` },
  { section: "AC Through R, L, C", title: "AC through a pure inductor — current lags voltage by 90°, XL=ωL",
    explanation: "For v = V0 sin(wt) applied across an ideal inductor L, the self-induced back-emf equals the applied voltage, giving i = (V0/wL) sin(wt - pi/2) = I0 sin(wt - pi/2). So current LAGS voltage by 90 degrees (a quarter cycle). The inductive reactance XL = wL plays the role of 'resistance' for the inductor and grows with frequency.",
    example: `L = 0.2 H, f = 50 Hz
XL = wL = 2*pi(50)(0.2) = 62.8 ohm

If V0 = 200 V:  I0 = V0/XL = 200/62.8 = 3.18 A, lagging V by 90°` },
  { section: "AC Through R, L, C", title: "AC through a pure capacitor — current leads voltage by 90°, XC=1/ωC",
    explanation: "For v = V0 sin(wt) applied across an ideal capacitor C, charge q = Cv, so i = dq/dt = C*V0*w*cos(wt) = I0 sin(wt + pi/2). Current LEADS voltage by 90 degrees. The capacitive reactance XC = 1/(wC) falls as frequency rises, and XC -> infinity as w -> 0 (a capacitor blocks steady DC).",
    example: `C = 10 uF, f = 50 Hz
XC = 1/(wC) = 1 / (2*pi(50)(10x10^-6)) = 1/(3.14x10^-3) = 318 ohm

If V0 = 200 V:  I0 = V0/XC = 200/318 = 0.63 A, leading V by 90°` },
  { section: "AC Through R, L, C", title: "Average power in pure inductor and pure capacitor circuits — zero",
    explanation: "Since current and voltage are 90 degrees out of phase in a pure L or pure C, the average power over a full cycle is P = Vrms*Irms*cos(phi) = Vrms*Irms*cos(90°) = 0. Energy is alternately stored in the inductor's magnetic field (or capacitor's electric field) and returned to the source each quarter cycle — none is permanently dissipated. This nondissipative current is called 'wattless'.",
    example: `P_avg = Vrms * Irms * cos(phi)
Pure L or pure C: phi = 90°  ->  cos(phi) = 0  ->  P_avg = 0

(energy oscillates between source and the L/C field; no net heat is produced)` },
  { section: "AC Through R, L, C", title: "Reactance vs resistance — key differences",
    explanation: "Unlike ohmic resistance R (frequency-independent, dissipates energy as heat), reactance (XL or XC) depends on frequency and stores/returns energy without dissipating it. XL = wL increases linearly with frequency (an inductor opposes high-frequency AC strongly); XC = 1/(wC) decreases with frequency (a capacitor opposes low-frequency/DC strongly). Both are measured in ohms.",
    example: `L = 1 H:
  at f = 50 Hz:  XL = 2*pi(50)(1)  = 314 ohm
  at f = 500 Hz: XL = 2*pi(500)(1) = 3140 ohm   (10x)

C = 1 uF:
  at f = 50 Hz:  XC = 1/(2*pi*50*1e-6)  = 3183 ohm
  at f = 500 Hz: XC = 1/(2*pi*500*1e-6) = 318 ohm  (1/10x)` },

  // ── LCR Series Circuit & Impedance ───────────────────────────────────────────
  { section: "LCR Series Circuit & Impedance", title: "LCR series circuit — phasor addition of voltages",
    explanation: "In a series LCR circuit the same current i flows through R, L and C, but their voltages have different phases relative to i: VR is in phase with i; VL leads i by 90°; VC lags i by 90° (so VL and VC point in opposite directions and partly cancel). The source voltage is the PHASOR (vector) sum: V = sqrt(VR^2 + (VL - VC)^2), not the simple algebraic sum.",
    example: `VR = 30 V, VL = 80 V, VC = 40 V   (all rms)

V = sqrt(VR^2 + (VL - VC)^2)
  = sqrt(30^2 + (80-40)^2) = sqrt(900 + 1600) = sqrt(2500) = 50 V` },
  { section: "LCR Series Circuit & Impedance", title: "Impedance of LCR series circuit — Z=√(R²+(XL−XC)²)",
    explanation: "Impedance Z is the AC analogue of resistance for a series LCR circuit — it combines the resistive part R with the net reactance (XL - XC), which are 90° apart, via Pythagorean addition: Z = sqrt(R^2 + (XL - XC)^2). The rms current is then Irms = Vrms / Z.",
    example: `R = 40 ohm, XL = 50 ohm, XC = 20 ohm

Z = sqrt(R^2 + (XL - XC)^2) = sqrt(40^2 + (50-20)^2)
  = sqrt(1600 + 900) = sqrt(2500) = 50 ohm

If Vrms = 100 V:  Irms = Vrms/Z = 100/50 = 2 A` },
  { section: "LCR Series Circuit & Impedance", title: "Phase angle between current and voltage in LCR circuit",
    explanation: "The phase angle phi by which the applied voltage leads the current satisfies tan(phi) = (XL - XC)/R. If XL > XC the circuit is net INDUCTIVE (voltage leads current, phi > 0). If XC > XL it is net CAPACITIVE (current leads voltage, phi < 0). If XL = XC, phi = 0 and the circuit behaves purely resistively (this is resonance).",
    example: `R = 40 ohm, XL = 50 ohm, XC = 20 ohm

tan(phi) = (XL - XC)/R = (50-20)/40 = 0.75
phi = tan^-1(0.75) ≈ 36.9°

Since XL > XC, the circuit is inductive: V leads I by ~36.9°` },
  { section: "LCR Series Circuit & Impedance", title: "Phasor diagram concept — representing AC quantities as rotating vectors",
    explanation: "A phasor is a vector of length equal to the peak (or rms) value, rotating counter-clockwise at angular frequency w; its projection onto a reference axis gives the instantaneous value of the sinusoidal quantity. Phasor diagrams let voltages/currents at different phases be combined by vector addition instead of solving trigonometric equations directly — this is exactly how Z and phi above are derived.",
    example: `Reference: current phasor I along 0°
  VR phasor:  along I direction         (0°, same phase as I)
  VL phasor:  90° AHEAD of I            (leads)
  VC phasor:  90° BEHIND I              (lags)

Resultant V = vector sum of VR and (VL - VC) at right angles
  -> gives both Z and phi from a single right triangle` },

  // ── Resonance & Power ────────────────────────────────────────────────────────
  { section: "Resonance & Power", title: "Condition for resonance in LCR series circuit — XL=XC",
    explanation: "Resonance occurs when the inductive and capacitive reactances are exactly equal (XL = XC), so the net reactance is zero and the circuit's impedance falls to its minimum possible value, Z = R. At resonance, current is IN PHASE with voltage (phi = 0) and is at its MAXIMUM possible value for the given source voltage.",
    example: `At resonance:  XL = XC   i.e.   wL = 1/(wC)

Z_min = sqrt(R^2 + 0^2) = R      (smallest impedance possible)
I_max = Vrms / R                 (largest current possible, any frequency)` },
  { section: "Resonance & Power", title: "Resonant frequency ω0 = 1/√(LC)",
    explanation: "Solving wL = 1/(wC) for w gives the resonant angular frequency w0 = 1/sqrt(LC), and resonant (linear) frequency f0 = 1/(2*pi*sqrt(LC)). Notably, w0 depends only on L and C — it does NOT depend on the resistance R.",
    example: `L = 0.5 H, C = 8 uF

w0 = 1/sqrt(LC) = 1/sqrt(0.5 x 8x10^-6) = 1/sqrt(4x10^-6) = 1/(2x10^-3) = 500 rad/s
f0 = w0/(2*pi) = 500/6.28 ≈ 79.6 Hz` },
  { section: "Resonance & Power", title: "Sharpness of resonance and Q factor",
    explanation: "The quality (Q) factor measures how sharp and selective the resonance peak is: Q = w0*L/R = 1/(w0*C*R) = (1/R)*sqrt(L/C). A HIGH Q (small R) gives a narrow, tall resonance curve — current drops off quickly as frequency moves away from f0 — which is exactly what's needed to select one station in a radio tuning circuit. Q also equals w0 divided by the resonance curve's bandwidth.",
    example: `Using L = 0.5 H, C = 8 uF, R = 10 ohm, w0 = 500 rad/s (from above)

Q = w0*L/R = (500)(0.5)/10 = 250/10 = 25

Higher Q -> sharper, more selective resonance peak` },
  { section: "Resonance & Power", title: "Power in AC circuit — P=VrmsIrmscosφ and power factor",
    explanation: "The average power actually delivered in an AC circuit is P = Vrms*Irms*cos(phi), where cos(phi) — the POWER FACTOR — accounts for the phase difference between voltage and current, since only the in-phase component of current does real work. For pure R, phi=0 so cos(phi)=1 (maximum power transfer); for pure L or C, phi=90° so cos(phi)=0 (no net power). In an LCR circuit, power factor = R/Z.",
    example: `Vrms = 200 V, Irms = 2 A, phi = 60°

P = Vrms * Irms * cos(phi) = 200 x 2 x cos(60°) = 400 x 0.5 = 200 W
Power factor = cos(60°) = 0.5` },
  { section: "Resonance & Power", title: "Wattless current",
    explanation: "When the current is 90° out of phase with the voltage (as in a pure L or pure C), the average power consumed is ZERO even though current is flowing — this non-power-dissipating component is called 'wattless' or idle current. In real circuits (e.g. inductive machinery on a transmission network), a large wattless current component still causes I^2*R heating losses in the connecting wires without doing any useful work — this is why a good (near-1) power factor is desirable industrially.",
    example: `Pure inductor circuit: Vrms = 100 V, Irms = 5 A, phi = 90°

P = Vrms * Irms * cos(90°) = 100 x 5 x 0 = 0 W   (all current is "wattless")

Yet this 5 A still flows through the supply wires, causing I^2*R
loss in the wiring even though it delivers zero net power` },

  // ── Transformer ───────────────────────────────────────────────────────────────
  { section: "Transformer", title: "Transformer — principle (mutual induction)",
    explanation: "A transformer works on the principle of MUTUAL INDUCTION: an alternating current in the primary coil creates a changing magnetic flux, which — guided through a laminated soft-iron core — links the secondary coil and induces an alternating emf in it. Because it needs a CHANGING flux, a transformer only works with AC; it cannot step up or down a steady DC voltage.",
    example: `Primary: Np turns, carries AC  ->  produces changing flux (Phi) in core
Secondary: Ns turns, linked with the same Phi

Induced emf in secondary:  Es = -Ns * (d(Phi)/dt)` },
  { section: "Transformer", title: "Turns ratio — Vs/Vp = Ns/Np",
    explanation: "For an ideal transformer, the ratio of secondary to primary voltage equals the ratio of secondary to primary turns: Vs/Vp = Ns/Np. If Ns > Np, it's a STEP-UP transformer (Vs > Vp); if Ns < Np, it's a STEP-DOWN transformer (Vs < Vp).",
    example: `Np = 200 turns, Ns = 2000 turns, Vp = 220 V

Vs = Vp * (Ns/Np) = 220 * (2000/200) = 220 * 10 = 2200 V   (step-up)` },
  { section: "Transformer", title: "Ideal transformer — current relation and power conservation",
    explanation: "For an ideal (100% efficient) transformer, input power equals output power: Vp*Ip = Vs*Is. Combined with Vs/Vp = Ns/Np, this gives Is/Ip = Np/Ns — the INVERSE of the voltage/turns ratio. So a step-up transformer (raises voltage) simultaneously steps DOWN the current, and vice versa.",
    example: `Step-up example: Ns/Np = 10, Vp = 220 V, Vs = 2200 V, Ip = 10 A

Power_in = Vp * Ip = 220 x 10 = 2200 W
Is = Power_in / Vs = 2200/2200 = 1 A

Check: Is/Ip = 1/10 = Np/Ns  ✓` },
  { section: "Transformer", title: "Energy losses in a real transformer and efficiency",
    explanation: "Real transformers are not 100% efficient because of: COPPER loss (I^2*R heating in the windings); IRON/core loss, split into HYSTERESIS loss (energy spent repeatedly reversing the core's magnetization) and EDDY CURRENT loss (induced currents circulating within the core itself, reduced by using a LAMINATED core); and FLUX LEAKAGE (not all flux links both coils). Efficiency eta = Pout/Pin x 100%; well-designed transformers reach about 95-99%.",
    example: `Pin = 2200 W,  total losses (copper + iron) = 110 W

Pout = Pin - losses = 2200 - 110 = 2090 W
eta = (Pout/Pin) x 100 = (2090/2200) x 100 = 95%` },
];

const S = {
  page:  { minHeight:"100vh", background:"linear-gradient(135deg,#0f172a,#1a1a2e)", fontFamily:"'Segoe UI',sans-serif", color:"#e2e8f0", padding:"18px 16px 80px",
    userSelect:"none", WebkitUserSelect:"none", MozUserSelect:"none", msUserSelect:"none" },
  back:  { color:"#94a3b8", fontSize:13, textDecoration:"none" },
  card:  { background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.11)", borderRadius:14, padding:"18px", marginBottom:16 },
  chip:  (active, color) => ({
    background: active ? color : "rgba(255,255,255,0.08)",
    color: active ? "#fff" : "#94a3b8",
    border:"none", borderRadius:20, padding:"6px 14px", fontSize:12.5, fontWeight:700, cursor:"pointer", marginRight:8, marginBottom:8,
  }),
  tag: (color) => ({ background:color, color:"#fff", borderRadius:20, padding:"2px 10px", fontSize:11, fontWeight:700, display:"inline-block" }),
  pre: { background:"#0b1120", border:"1px solid rgba(255,255,255,0.08)", borderRadius:10, padding:"12px 14px", fontSize:12.5, lineHeight:1.6, color:"#a5f3b8", overflowX:"auto", fontFamily:"'Fira Code','Consolas',monospace", whiteSpace:"pre" },
  option: (bg) => ({ display:"block", width:"100%", textAlign:"left", background:bg, border:"1px solid rgba(255,255,255,0.1)", borderRadius:8, padding:"9px 12px", color:"#e2e8f0", fontSize:13, marginBottom:7, cursor:"pointer" }),
  iconBtn: (bg) => ({ background:bg, border:"none", borderRadius:8, width:30, height:30, fontSize:14, lineHeight:1, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", flex:"none" }),
};

// ── One interactive objective question — click an option to check it ──────────
function QuizQuestion({ item, index, onAnswer }) {
  const [selected, setSelected] = useState(null);
  const answered = selected !== null;

  const pick = (i) => {
    if (answered) return;
    setSelected(i);
    onAnswer && onAnswer(i === item.correct);
  };

  return (
    <div style={S.card}>
      <span style={S.tag(SECTION_COLORS[item.section])}>{item.section}</span>
      <span style={{ fontSize:11, color:"#64748b", marginLeft:8 }}>{item.topic}</span>
      <div style={{ fontWeight:700, fontSize:14, color:"#fff", marginTop:8, marginBottom:10 }}>{index + 1}. {item.q}</div>
      {item.options.map((opt, i) => {
        let bg = "rgba(255,255,255,0.05)";
        if (answered) {
          if (i === item.correct) bg = "rgba(34,197,94,0.28)";
          else if (i === selected) bg = "rgba(239,68,68,0.28)";
        }
        return (
          <button key={i} style={S.option(bg)} onClick={() => pick(i)} disabled={answered}>
            {String.fromCharCode(65 + i)}. {opt}
          </button>
        );
      })}
      {answered && (
        <div style={{ fontSize:12.5, color:"#94a3b8", marginTop:8, lineHeight:1.6 }}>
          {selected === item.correct ? "✅ Correct — " : "❌ Not quite — "}{item.explain}
        </div>
      )}
    </div>
  );
}

export default function NeetPhysicsCh22Page() {
  const [mode, setMode] = useState("guide"); // "guide" | "practice"
  const [activeSection, setActiveSection] = useState("All");
  const [score, setScore] = useState({ correct: 0, answered: 0 });

  // ── Bookmarks — same 🔖 pattern used elsewhere on the site, persisted locally ──
  const [bookmarks, setBookmarks] = useState(() => {
    try {
      const raw = window.localStorage.getItem("neetPhysicsCh22Bookmarks");
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  const [showBookmarkedOnly, setShowBookmarkedOnly] = useState(false);

  useEffect(() => {
    try { window.localStorage.setItem("neetPhysicsCh22Bookmarks", JSON.stringify(bookmarks)); } catch { /* ignore */ }
  }, [bookmarks]);

  const toggleBookmark = (title) => {
    setBookmarks(prev => prev.includes(title) ? prev.filter(t => t !== title) : [...prev, title]);
  };

  // ── Read aloud — same speechSynthesis pattern used on Reading Notes / TNPSC ──
  const [speakingIdx, setSpeakingIdx] = useState(null);

  useEffect(() => () => { window.speechSynthesis && window.speechSynthesis.cancel(); }, []);

  const speakConcept = (c, idx) => {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    if (speakingIdx === idx) { setSpeakingIdx(null); return; }
    const u = new window.SpeechSynthesisUtterance(`${c.title}. ${c.explanation}`);
    u.rate = 0.95;
    u.onend = () => setSpeakingIdx(null);
    u.onerror = () => setSpeakingIdx(null);
    window.speechSynthesis.speak(u);
    setSpeakingIdx(idx);
  };

  // ── Share — native share sheet, falling back to WhatsApp like the other pages ──
  const shareConcept = (c, idx) => {
    const url = `${window.location.origin}${window.location.pathname}#concept-${idx}`;
    const text = `🧪 NEET Physics — ${c.title}\n\n${c.explanation}\n\n${url}`;
    if (navigator.share) {
      navigator.share({ title: `NEET Physics — ${c.title}`, text, url }).catch(() => {});
    } else {
      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
    }
  };

  const filtered = useMemo(() => {
    let list = activeSection === "All" ? CONCEPTS : CONCEPTS.filter(c => c.section === activeSection);
    if (showBookmarkedOnly) list = list.filter(c => bookmarks.includes(c.title));
    return list;
  }, [activeSection, showBookmarkedOnly, bookmarks]);

  const filteredQuestions = useMemo(() => (
    activeSection === "All" ? NEET_PHYSICS_CH22_QUESTIONS : NEET_PHYSICS_CH22_QUESTIONS.filter(q => q.section === activeSection)
  ), [activeSection]);

  const handleModeChange = (m) => {
    setMode(m);
    setScore({ correct: 0, answered: 0 });
  };

  const handleAnswer = (isCorrect) => {
    setScore(s => ({ correct: s.correct + (isCorrect ? 1 : 0), answered: s.answered + 1 }));
  };

  return (
    <div
      style={S.page}
      onCopy={(e) => e.preventDefault()}
      onCut={(e) => e.preventDefault()}
      onContextMenu={(e) => e.preventDefault()}
    >
      <Link to="/neet-physics" style={S.back}>← Back to NEET Physics Menu</Link>

      <div style={{ textAlign:"center", padding:"14px 0 10px" }}>
        <div style={{ fontSize:26, fontWeight:800, color:"#fff" }}>🧪 NEET Physics — Chapter 22</div>
        <div style={{ fontSize:15, fontWeight:700, color:"#fbbf24", marginTop:4 }}>Alternating Current</div>
        <div style={{ fontSize:12.5, color:"#94a3b8", marginTop:6 }}>
          NCERT Class 12 Physics · {CONCEPTS.length} concepts explained · {NEET_PHYSICS_CH22_QUESTIONS.length} practice questions · NOT YET human-reviewed, verify against NCERT before exam use
        </div>
      </div>

      {/* Guide / Practice mode toggle */}
      <div style={{ textAlign:"center", margin:"14px 0 8px" }}>
        <button style={S.chip(mode==="guide", "#0f766e")} onClick={() => handleModeChange("guide")}>📘 Concept Guide</button>
        <button style={S.chip(mode==="practice", "#b45309")} onClick={() => handleModeChange("practice")}>🎯 Practice Questions ({NEET_PHYSICS_CH22_QUESTIONS.length})</button>
      </div>

      {/* Section filter chips */}
      <div style={{ textAlign:"center", margin:"6px 0 18px" }}>
        <button style={S.chip(activeSection==="All", "#334155")} onClick={() => setActiveSection("All")}>All</button>
        {SECTIONS.map(s => (
          <button key={s} style={S.chip(activeSection===s, SECTION_COLORS[s])} onClick={() => setActiveSection(s)}>{s}</button>
        ))}
      </div>

      {mode === "practice" ? (
        <>
          {score.answered > 0 && (
            <div style={{ textAlign:"center", fontSize:13, fontWeight:700, color:"#fbbf24", marginBottom:14 }}>
              Score: {score.correct} / {score.answered} answered correctly
            </div>
          )}
          {filteredQuestions.map((item, i) => (
            <QuizQuestion key={i} item={item} index={i} onAnswer={handleAnswer} />
          ))}
        </>
      ) : (
      <>
      {/* ── Bookmark filter ── */}
      <div style={{ textAlign:"center", margin:"0 0 14px" }}>
        <button style={S.chip(showBookmarkedOnly, "#db2777")} onClick={() => setShowBookmarkedOnly(v => !v)}>
          🔖 {showBookmarkedOnly ? "Showing Bookmarked" : "Bookmarked"} ({bookmarks.length})
        </button>
      </div>

      {/* ── Quick reference table ── */}
      <div style={S.card}>
        <div style={{ fontWeight:800, fontSize:16, color:"#fff", marginBottom:12 }}>📋 Quick Reference Table</div>
        <div style={{ overflowX:"auto" }}>
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
            <thead>
              <tr style={{ borderBottom:"2px solid rgba(255,255,255,0.15)" }}>
                <th style={{ textAlign:"left", padding:"6px 8px", color:"#94a3b8" }}>#</th>
                <th style={{ textAlign:"left", padding:"6px 8px", color:"#94a3b8" }}>Section</th>
                <th style={{ textAlign:"left", padding:"6px 8px", color:"#94a3b8" }}>Topic</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c, i) => (
                <tr key={i} style={{ borderBottom:"1px solid rgba(255,255,255,0.06)" }}>
                  <td style={{ padding:"7px 8px", color:"#64748b" }}>{i + 1}</td>
                  <td style={{ padding:"7px 8px" }}><span style={S.tag(SECTION_COLORS[c.section])}>{c.section}</span></td>
                  <td style={{ padding:"7px 8px" }}>
                    <a
                      href={`#concept-${i}`}
                      style={{ color:"#38bdf8", textDecoration:"underline", fontWeight:600 }}
                      onMouseEnter={(e) => { e.currentTarget.style.color = "#7dd3fc"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = "#38bdf8"; }}
                    >
                      {c.title}
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Detailed concept cards ── */}
      {filtered.map((c, i) => {
        const isBookmarked = bookmarks.includes(c.title);
        const isSpeaking = speakingIdx === i;
        return (
        <div key={i} id={`concept-${i}`} style={S.card}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:8, flexWrap:"wrap" }}>
            <span style={S.tag(SECTION_COLORS[c.section])}>{c.section}</span>
            <div style={{ display:"flex", gap:6 }}>
              <button
                title={isSpeaking ? "Stop reading" : "Read aloud"}
                onClick={() => speakConcept(c, i)}
                style={S.iconBtn(isSpeaking ? "#2563eb" : "rgba(37,99,235,0.22)")}
              >{isSpeaking ? "⏹" : "🔊"}</button>
              <button
                title={isBookmarked ? "Remove bookmark" : "Bookmark"}
                onClick={() => toggleBookmark(c.title)}
                style={S.iconBtn(isBookmarked ? "#db2777" : "rgba(219,39,119,0.22)")}
              >{isBookmarked ? "🔖" : "📌"}</button>
              <button
                title="Share"
                onClick={() => shareConcept(c, i)}
                style={S.iconBtn("rgba(255,255,255,0.12)")}
              >↗</button>
            </div>
          </div>
          <div style={{ fontWeight:800, fontSize:16, color:"#fff", marginTop:8 }}>{i + 1}. {c.title}</div>
          <div style={{ fontSize:13.5, color:"#cbd5e1", lineHeight:1.7, marginTop:8 }}>{c.explanation}</div>
          <div style={{ fontSize:11, fontWeight:700, color:"#94a3b8", marginTop:12, marginBottom:6, letterSpacing:0.4 }}>EXAMPLE / WORKED CALCULATION</div>
          <pre style={S.pre}><code>{c.example}</code></pre>
        </div>
        );
      })}
      </>
      )}
    </div>
  );
}
